// Pure, coercion-aware type-mismatch producer (Item 3e + A1 engine-strict rules).
//
// Input: already-resolved comparisons (column operands carry a coarse
// `OperandLiteralSqlType`). Output: `TypeCheckMarker[]`. No I/O, no Monaco, no
// schema lookup — fully unit-testable. The cross-engine coercion matrix it
// encodes is empirically verified against all 5 live engines; see
// Docs/TypeCheckDiagnostics.md.
//
// The rules are a registry of small `(comparison, engine) => marker | null`
// predicates. Each comparison runs through them in order; the first match wins
// (a comparison mismatches at most one way). Adding an engine-strict rule is a
// localised append — the dispatch never changes.
//
// Rules shipped:
//   numeric-vs-nonnumeric-string  — ALL engines. numeric col vs a string
//       literal that isn't a number (`WHERE total = 'free'`). Errors on
//       PG/MSSQL/Oracle, silently matches nothing on MySQL/SQLite.
//   text-vs-number                — PostgreSQL only. text col vs a numeric
//       literal (`WHERE code = 5`). PG has no implicit text↔number cast, so
//       this is a *plan-time* error regardless of row data (verified live).
//   boolean-vs-nonboolean         — PostgreSQL only. boolean col vs a numeric
//       literal, or a string PG won't accept as a boolean (`active = 'abc'`).
//       `active = 'yes'` is accepted, so it is never flagged.

import type { SqlEngine } from '@/features/sql-grammar/parseOptions';

import { quotedStringIsBoolean, quotedStringIsNumeric } from './numericLiteral';
import type {
  ResolvedComparison,
  ResolvedOperand,
  TypeCheckMarker,
} from './types';

// Binary scalar comparisons only. `BETWEEN` / `IN` / `LIKE` / `IS` have
// multi-operand or non-value RHS shapes the parser does not split into a
// single operand, so v1 leaves them alone.
const SCALAR_COMPARISON_OPS: ReadonlySet<string> = new Set([
  '=', '<>', '!=', '<', '>', '<=', '>=',
]);

type Rule = (c: ResolvedComparison, engine: SqlEngine) => TypeCheckMarker | null;

const RULES: readonly Rule[] = [
  checkNumericVsNonNumericString,
  checkTextColumnVsNumber,
  checkBooleanColumnVsNonBoolean,
];

export function buildTypeCheckDiagnostics(
  comparisons: readonly ResolvedComparison[],
  engine: SqlEngine,
): TypeCheckMarker[] {
  const out: TypeCheckMarker[] = [];
  for (const c of comparisons) {
    if (!c.rhs) continue; // unary (IS NULL etc.)
    if (!SCALAR_COMPARISON_OPS.has(c.operator)) continue;
    for (const rule of RULES) {
      const marker = rule(c, engine);
      if (marker) { out.push(marker); break; } // first match per comparison
    }
  }
  return out;
}

function marker(
  c: ResolvedComparison,
  ruleId: TypeCheckMarker['ruleId'],
  message: string,
): TypeCheckMarker {
  return {
    startOffset: c.startOffset,
    endOffset: c.endOffset,
    severity: 'warning',
    code: 'intellisense.type-mismatch',
    ruleId,
    message,
  };
}

// ── Rule 1: numeric column vs non-numeric string literal (all engines) ──────
function checkNumericVsNonNumericString(
  c: ResolvedComparison,
  engine: SqlEngine,
): TypeCheckMarker | null {
  const pair = matchSides(c, (col) => col.sqlType === 'numeric', isNonNumericStringLiteral);
  if (!pair) return null;
  const lit = clip(pair.literal.text);
  return marker(c, 'numeric-vs-nonnumeric-string',
    `Type mismatch: numeric column \`${pair.column.name}\` is compared to the non-numeric text ${lit} — ${numericEffect(engine)}`);
}

// ── Rule 2: text column vs numeric literal (PostgreSQL only) ────────────────
function checkTextColumnVsNumber(
  c: ResolvedComparison,
  engine: SqlEngine,
): TypeCheckMarker | null {
  if (engine !== 'postgres') return null;
  const pair = matchSides(c, (col) => col.sqlType === 'text', isNumericLiteral);
  if (!pair) return null;
  return marker(c, 'text-vs-number',
    `Type mismatch: text column \`${pair.column.name}\` is compared to the number ${clip(pair.literal.text)} — PostgreSQL has no implicit text↔number cast and raises "operator does not exist" at plan time. Quote the value (e.g. '${stripNum(pair.literal.text)}') or cast the column.`);
}

// ── Rule 3: boolean column vs non-boolean literal (PostgreSQL only) ─────────
function checkBooleanColumnVsNonBoolean(
  c: ResolvedComparison,
  engine: SqlEngine,
): TypeCheckMarker | null {
  if (engine !== 'postgres') return null;
  const pair = matchSides(
    c,
    (col) => col.sqlType === 'boolean',
    (lit) => isNumericLiteral(lit) || (lit.sqlType === 'text' && !quotedStringIsBoolean(lit.text)),
  );
  if (!pair) return null;
  return marker(c, 'boolean-vs-nonboolean',
    `Type mismatch: boolean column \`${pair.column.name}\` is compared to ${clip(pair.literal.text)}, which PostgreSQL does not accept as a boolean. Use true/false (or 't'/'f', 'yes'/'no', '1'/'0').`);
}

// ── Shared matching ────────────────────────────────────────────────────────
type Pair = {
  readonly column: Extract<ResolvedOperand, { kind: 'column' }>;
  readonly literal: Extract<ResolvedOperand, { kind: 'literal' }>;
};

// Column on either side (predicate `colOk`) vs a literal on the other side
// (predicate `litOk`). Column-vs-literal only; expression/literal-literal pairs
// are out of scope (v1).
function matchSides(
  c: ResolvedComparison,
  colOk: (col: Extract<ResolvedOperand, { kind: 'column' }>) => boolean,
  litOk: (lit: Extract<ResolvedOperand, { kind: 'literal' }>) => boolean,
): Pair | null {
  return one(c.lhs, c.rhs!, colOk, litOk) ?? one(c.rhs!, c.lhs, colOk, litOk);
}

function one(
  a: ResolvedOperand,
  b: ResolvedOperand,
  colOk: (col: Extract<ResolvedOperand, { kind: 'column' }>) => boolean,
  litOk: (lit: Extract<ResolvedOperand, { kind: 'literal' }>) => boolean,
): Pair | null {
  if (a.kind !== 'column' || !colOk(a)) return null;
  if (b.kind !== 'literal' || !litOk(b)) return null;
  return { column: a, literal: b };
}

function isNonNumericStringLiteral(lit: Extract<ResolvedOperand, { kind: 'literal' }>): boolean {
  return lit.sqlType === 'text' && !quotedStringIsNumeric(lit.text);
}

function isNumericLiteral(lit: Extract<ResolvedOperand, { kind: 'literal' }>): boolean {
  return lit.sqlType === 'integer' || lit.sqlType === 'numeric';
}

function clip(text: string): string {
  return text.length > 40 ? `${text.slice(0, 39)}…` : text;
}

// `5` → `5` for the "quote the value" hint (strip a leading +/- is left as-is).
function stripNum(text: string): string {
  return text.trim();
}

function numericEffect(engine: SqlEngine): string {
  switch (engine) {
    case 'postgres':
      return 'PostgreSQL raises "invalid input syntax" at runtime.';
    case 'mssql':
      return 'SQL Server raises a conversion error at runtime.';
    case 'oracle':
      return 'Oracle raises ORA-01722 (invalid number) at runtime.';
    case 'mysql':
      return 'MySQL coerces it to 0 (truncation warning) and matches nothing.';
    case 'sqlite':
      return 'SQLite compares numerically and matches nothing.';
  }
}
